import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ShoppingCart, ChevronRight, Search, Clock, AlertTriangle,
  Truck, X, Loader2, RotateCcw,
} from 'lucide-react';
import { PageHeader, StatusBadge, EmptyState, DataTableSkeleton } from '../components/ui';
import { useToast, ToastContainer } from '../components/ui';
import { API_BASE } from '../api';
import { useDemo } from '../context/DemoContext';
import { DEMO_VENDOR_ORDERS } from '../demo/data';

type OrderStatus = 'new' | 'processing' | 'shipped' | 'delivered' | 'exception' | 'cancelled';
type ReturnStatus = 'pending' | 'authorized' | 'in_transit' | 'received' | 'resolved' | 'rejected';

interface VendorOrder {
  id: number;
  shopify_order_id: string | null;
  status: OrderStatus;
  fulfillment_deadline: string | null;
  total_amount: string | null;
  line_items: unknown;
  internal_notes: string | null;
  tracking_number?: string | null;
  created_at: string;
  updated_at: string;
}

interface ReturnRequest {
  id: number;
  vendor_order_id: number;
  shopify_order_id: string;
  status: ReturnStatus;
  reason: string;
  items: unknown[];
  refund_amount: number;
  created_at: string;
}

const statusVariantMap: Record<OrderStatus, 'success' | 'warning' | 'error' | 'info' | 'neutral'> = {
  new: 'info', processing: 'warning', shipped: 'success', delivered: 'success', exception: 'error', cancelled: 'neutral',
};

const returnStatusVariant: Record<ReturnStatus, 'success' | 'warning' | 'error' | 'info' | 'neutral'> = {
  pending: 'warning', authorized: 'info', in_transit: 'info', received: 'success', resolved: 'success', rejected: 'error',
};

const DEMO_RETURNS: ReturnRequest[] = [
  { id: 1, vendor_order_id: 2001, shopify_order_id: '#20001', status: 'pending', reason: 'Wrong size', items: [], refund_amount: 76.50, created_at: new Date(Date.now() - 2 * 86400000).toISOString() },
  { id: 2, vendor_order_id: 2003, shopify_order_id: '#20003', status: 'authorized', reason: 'Defective', items: [], refund_amount: 148.00, created_at: new Date(Date.now() - 5 * 86400000).toISOString() },
  { id: 3, vendor_order_id: 2004, shopify_order_id: '#20004', status: 'received', reason: 'Changed mind', items: [], refund_amount: 55.00, created_at: new Date(Date.now() - 8 * 86400000).toISOString() },
];

const TAB_STATUS_MAP: Record<string, OrderStatus[]> = {
  active: ['new', 'processing'],
  shipped: ['shipped'],
  completed: ['delivered'],
};

export default function VendorOrders() {
  const { t, i18n } = useTranslation(['orders', 'vendor', 'common']);
  const { toasts, addToast, removeToast } = useToast();
  const navigate = useNavigate();
  const { isDemoMode } = useDemo();
  const [searchParams, setSearchParams] = useSearchParams();
  const locale = i18n.language === 'zh' ? 'zh-CN' : 'en-US';

  const activeTab = (['active', 'shipped', 'completed', 'returns'].includes(searchParams.get('tab') || '') ? searchParams.get('tab') : 'active') as string;
  const setTab = (tab: string) => {
    const p = new URLSearchParams(); if (tab !== 'active') p.set('tab', tab);
    setSearchParams(p, { replace: true });
  };

  const [orders, setOrders] = useState<VendorOrder[]>([]);
  const [returns, setReturns] = useState<ReturnRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [shipModal, setShipModal] = useState<{ orderId: number } | null>(null);
  const [shipTracking, setShipTracking] = useState('');
  const [shipCarrier, setShipCarrier] = useState('');
  const [shipLoading, setShipLoading] = useState(false);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  const fetchOrders = async () => {
    if (isDemoMode) { setOrders(DEMO_VENDOR_ORDERS as VendorOrder[]); setLoading(false); return; }
    try {
      const res = await fetch(`${API_BASE}/vendor/orders`, { credentials: 'include' });
      const json = await res.json();
      if (res.ok) setOrders(json.data || []);
    } catch { addToast(t('common:error'), 'error'); }
    finally { setLoading(false); }
  };

  const fetchReturns = async () => {
    if (isDemoMode) { setReturns(DEMO_RETURNS); setLoading(false); return; }
    try {
      const res = await fetch(`${API_BASE}/vendor/returns`, { credentials: 'include' });
      const json = await res.json();
      if (res.ok) setReturns(json.data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    setLoading(true);
    if (activeTab === 'returns') fetchReturns();
    else fetchOrders();
  }, [isDemoMode, activeTab]);

  const handleShip = async () => {
    if (!shipModal || !shipTracking) return;
    setShipLoading(true);
    try {
      const res = await fetch(`${API_BASE}/vendor/orders/${shipModal.orderId}/ship`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tracking_number: shipTracking, carrier: shipCarrier || undefined }),
      });
      if (res.ok) { addToast(t('common:success'), 'success'); setShipModal(null); setShipTracking(''); setShipCarrier(''); fetchOrders(); }
      else { const j = await res.json().catch(() => ({})); addToast((j as { error?: string }).error || t('common:error'), 'error'); }
    } catch { addToast(t('common:error'), 'error'); }
    finally { setShipLoading(false); }
  };

  const handleReturnAction = async (returnId: number, action: string) => {
    try {
      const res = await fetch(`${API_BASE}/vendor/returns/${returnId}/${action}`, { method: 'POST', credentials: 'include' });
      if (res.ok) { addToast(t('common:success'), 'success'); fetchReturns(); }
    } catch { addToast(t('common:error'), 'error'); }
  };

  const isOverdue = (d: string | null) => d ? new Date(d) < new Date() : false;
  const isAtRisk = (d: string | null) => { if (!d) return false; const r = new Date(d).getTime() - Date.now(); return r > 0 && r < 2 * 86400000; };
  const getLineItemCount = (o: VendorOrder) => Array.isArray(o.line_items) ? o.line_items.length : 0;

  const tabStatuses = TAB_STATUS_MAP[activeTab] || [];
  const visibleOrders = orders
    .filter(o => tabStatuses.length === 0 || tabStatuses.includes(o.status))
    .filter(o => {
      if (!debouncedSearch) return true;
      const q = debouncedSearch.toLowerCase();
      return (o.shopify_order_id || '').toLowerCase().includes(q) || String(o.id).includes(q) || (o.tracking_number || '').toLowerCase().includes(q);
    });

  const tabBtn = (key: string, label: string, count?: number) => (
    <button onClick={() => setTab(key)}
      className={`px-4 py-2 text-sm font-medium transition-colors relative ${activeTab === key ? 'text-[#FFD400]' : 'text-white/45 hover:text-white/70'}`}>
      {label}{count != null && count > 0 && <span className="ml-1.5 text-[10px] text-white/30">({count})</span>}
      {activeTab === key && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#FFD400]" />}
    </button>
  );

  const activeCount = orders.filter(o => ['new', 'processing'].includes(o.status)).length;
  const shippedCount = orders.filter(o => o.status === 'shipped').length;
  const completedCount = orders.filter(o => o.status === 'delivered').length;

  return (
    <div className="space-y-6">
      <PageHeader title={t('orders:title')} />

      {/* Tabs */}
      <div className="flex gap-1" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        {tabBtn('active', t('orders:tabs.active'), activeCount)}
        {tabBtn('shipped', t('orders:tabs.shipped'), shippedCount)}
        {tabBtn('completed', t('orders:tabs.completed'), completedCount)}
        {tabBtn('returns', t('orders:tabs.returns'))}
      </div>

      {activeTab !== 'returns' ? (
        <>
          {/* Search */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-xs">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-white/30" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} className="input pl-8 text-sm w-full" placeholder={t('orders:searchPlaceholder')} />
            </div>
          </div>

          {/* Order table */}
          {loading ? <DataTableSkeleton rows={5} cols={6} /> : visibleOrders.length === 0 ? (
            <EmptyState icon={<ShoppingCart className="w-8 h-8" />} message={t('orders:empty')} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <colgroup>
                  <col style={{ width: '15%' }} />
                  <col style={{ width: '15%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '15%' }} />
                  <col style={{ width: '15%' }} />
                  <col style={{ width: '4%' }} />
                </colgroup>
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    <th className="text-left px-3 py-2.5 text-[11px] text-white/40 uppercase">{t('orders:orderId')}</th>
                    <th className="text-left px-3 py-2.5 text-[11px] text-white/40 uppercase">{t('orders:orderDate')}</th>
                    <th className="text-center px-3 py-2.5 text-[11px] text-white/40 uppercase">{t('orders:items')}</th>
                    <th className="text-right px-3 py-2.5 text-[11px] text-white/40 uppercase">{t('orders:totalPrice')}</th>
                    <th className="text-center px-3 py-2.5 text-[11px] text-white/40 uppercase">{t('common:status')}</th>
                    <th className="text-center px-3 py-2.5 text-[11px] text-white/40 uppercase">{t('orders:fulfillmentDeadline')}</th>
                    <th className="text-center px-3 py-2.5 text-[11px] text-white/40 uppercase">{t('common:actions')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {visibleOrders.map(order => (
                    <tr key={order.id} onClick={() => navigate(`/vendor/orders/${order.id}`)}
                      className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors cursor-pointer">
                      <td className="px-3 py-2.5 text-white/80 font-medium">#{order.shopify_order_id || order.id}</td>
                      <td className="px-3 py-2.5 text-white/40">{new Date(order.created_at).toLocaleDateString(locale)}</td>
                      <td className="px-3 py-2.5 text-white/50 text-center">{getLineItemCount(order)}</td>
                      <td className="px-3 py-2.5 text-right text-white font-medium">{order.total_amount ? `$${parseFloat(order.total_amount).toFixed(2)}` : '—'}</td>
                      <td className="px-3 py-2.5 text-center"><StatusBadge variant={statusVariantMap[order.status]}>{t(`orders:status.${order.status}`)}</StatusBadge></td>
                      <td className="px-3 py-2.5 text-center">
                        {order.fulfillment_deadline ? (
                          isOverdue(order.fulfillment_deadline) ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-[#f87171]"><AlertTriangle className="w-3 h-3" />{t('orders:deadline.overdue')}</span>
                          ) : isAtRisk(order.fulfillment_deadline) ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-[#eab308]"><Clock className="w-3 h-3" />{t('orders:deadline.atRisk')}</span>
                          ) : (
                            <span className="text-[10px] text-[#4ade80] font-medium">{t('orders:deadline.onTime')}</span>
                          )
                        ) : <span className="text-white/15">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-center" onClick={e => e.stopPropagation()}>
                        {order.status === 'processing' && (
                          <button onClick={() => setShipModal({ orderId: order.id })}
                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-1 transition-colors hover:bg-[#FFD400]/20 text-[#FFD400]"
                            style={{ border: '1px solid rgba(255,212,0,0.3)', borderRadius: '2px' }}>
                            <Truck className="w-3 h-3" />{t('orders:quickShip')}
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2.5"><ChevronRight className="w-4 h-4 text-white/15" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        /* Returns tab */
        loading ? <DataTableSkeleton rows={3} cols={6} /> : returns.length === 0 ? (
          <EmptyState icon={<RotateCcw className="w-8 h-8" />} message={t('vendor:returns.empty')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <colgroup>
                <col style={{ width: '10%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '18%' }} />
              </colgroup>
              <thead>
                <tr className="border-b border-white/[0.06]">
                  <th className="text-left px-3 py-2.5 text-[11px] text-white/40 uppercase">{t('vendor:returns.returnId')}</th>
                  <th className="text-left px-3 py-2.5 text-[11px] text-white/40 uppercase">{t('orders:orderId')}</th>
                  <th className="text-center px-3 py-2.5 text-[11px] text-white/40 uppercase">{t('common:status')}</th>
                  <th className="text-left px-3 py-2.5 text-[11px] text-white/40 uppercase">{t('vendor:returns.reason')}</th>
                  <th className="text-right px-3 py-2.5 text-[11px] text-white/40 uppercase">{t('vendor:returns.refundAmount')}</th>
                  <th className="text-left px-3 py-2.5 text-[11px] text-white/40 uppercase">{t('common:date')}</th>
                  <th className="text-center px-3 py-2.5 text-[11px] text-white/40 uppercase">{t('common:actions')}</th>
                </tr>
              </thead>
              <tbody>
                {returns.map(r => (
                  <tr key={r.id} className="border-b border-white/[0.03]">
                    <td className="px-3 py-2.5 text-white/60">#{r.id}</td>
                    <td className="px-3 py-2.5 text-white/70 font-medium">{r.shopify_order_id}</td>
                    <td className="px-3 py-2.5 text-center"><StatusBadge variant={returnStatusVariant[r.status]}>{t(`vendor:returns.status.${r.status}`)}</StatusBadge></td>
                    <td className="px-3 py-2.5 text-white/50">{r.reason}</td>
                    <td className="px-3 py-2.5 text-right text-white font-medium">${r.refund_amount.toFixed(2)}</td>
                    <td className="px-3 py-2.5 text-white/40 text-xs">{new Date(r.created_at).toLocaleDateString(locale)}</td>
                    <td className="px-3 py-2.5 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {r.status === 'pending' && (
                          <>
                            <button onClick={() => handleReturnAction(r.id, 'authorize')} className="text-[10px] font-bold uppercase px-2 py-0.5 text-[#4ade80] hover:bg-[#4ade80]/10 transition-colors" style={{ border: '1px solid rgba(74,222,128,0.3)', borderRadius: '2px' }}>{t('vendor:returns.authorize')}</button>
                            <button onClick={() => handleReturnAction(r.id, 'reject')} className="text-[10px] font-bold uppercase px-2 py-0.5 text-[#f87171] hover:bg-[#f87171]/10 transition-colors" style={{ border: '1px solid rgba(248,113,113,0.3)', borderRadius: '2px' }}>{t('vendor:returns.reject')}</button>
                          </>
                        )}
                        {r.status === 'authorized' && (
                          <button onClick={() => handleReturnAction(r.id, 'receive')} className="text-[10px] font-bold uppercase px-2 py-0.5 text-[#3b82f6] hover:bg-[#3b82f6]/10 transition-colors" style={{ border: '1px solid rgba(59,130,246,0.3)', borderRadius: '2px' }}>{t('vendor:returns.markReceived')}</button>
                        )}
                        {r.status === 'received' && (
                          <button onClick={() => handleReturnAction(r.id, 'resolve')} className="text-[10px] font-bold uppercase px-2 py-0.5 text-[#a78bfa] hover:bg-[#a78bfa]/10 transition-colors" style={{ border: '1px solid rgba(167,139,250,0.3)', borderRadius: '2px' }}>{t('vendor:returns.resolve')}</button>
                        )}
                        {(['resolved', 'rejected'] as ReturnStatus[]).includes(r.status) && <span className="text-xs text-white/25">—</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* Ship modal */}
      {shipModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setShipModal(null)}>
          <div className="w-full max-w-sm p-5 space-y-4" onClick={e => e.stopPropagation()}
            style={{ backgroundColor: '#141414', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '4px' }}>
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-white">{t('orders:enterTracking')}</h3>
              <button onClick={() => setShipModal(null)} className="text-white/30 hover:text-white/60"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-[11px] text-white/40 uppercase tracking-wider font-medium">{t('orders:trackingNumber')}</label>
                <input value={shipTracking} onChange={e => setShipTracking(e.target.value)} className="input w-full" autoFocus />
              </div>
              <div className="space-y-1">
                <label className="text-[11px] text-white/40 uppercase tracking-wider font-medium">{t('orders:carrier')}</label>
                <select value={shipCarrier} onChange={e => setShipCarrier(e.target.value)} className="input w-full">
                  <option value="">—</option>
                  <option value="USPS">USPS</option>
                  <option value="UPS">UPS</option>
                  <option value="FedEx">FedEx</option>
                  <option value="DHL">DHL</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShipModal(null)} className="btn-ghost text-sm">{t('common:cancel')}</button>
              <button onClick={handleShip} disabled={!shipTracking || shipLoading} className="btn-primary text-sm flex items-center gap-1.5">
                {shipLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Truck className="w-3.5 h-3.5" />}
                {t('orders:markShipped')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
}
